/**
 * ViewerConfigPanel — Slide-out admin panel for configuring viewer settings.
 *
 * Tabs: Brand, Parts, Tools, Support, Files
 * Uses useViewerConfig() hook for fetch/save operations (brand, support, files).
 * Uses useProjectItems() hook for tools/parts CRUD via Items API.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useViewerConfig } from '../../hooks/useViewerConfig';
import { useProjectItems } from '../../hooks/useProjectItems';
import type {
  ViewerBrandConfig,
  ViewerConfigUpsertPayload,
  ViewerFileConfig,
  ViewerPartConfig,
  ViewerSupportConfig,
  ViewerToolConfig,
} from '../../server/admin/viewer-config/types';
import { BrandConfigTab } from './viewer-config/BrandConfigTab';
import { PartsConfigTab } from './viewer-config/PartsConfigTab';
import { ToolsConfigTab } from './viewer-config/ToolsConfigTab';
import { SupportConfigTab } from './viewer-config/SupportConfigTab';
import { FilesConfigTab } from './viewer-config/FilesConfigTab';
import './viewer-config/viewer-config.css';

// ── Types ────────────────────────────────────────────────────────

type TabKey = 'brand' | 'parts' | 'tools' | 'support' | 'files';

interface ViewerConfigPanelProps {
  companyId: string;
  productId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
}

const TABS: { key: TabKey; label: string }[] = [
  { key: 'brand', label: 'Brand' },
  { key: 'parts', label: 'Parts' },
  { key: 'tools', label: 'Tools' },
  { key: 'support', label: 'Support' },
  { key: 'files', label: 'Files' },
];

// ── Component ────────────────────────────────────────────────────

export function ViewerConfigPanel({
  companyId,
  productId,
  isOpen,
  onClose,
  onSaveSuccess,
}: ViewerConfigPanelProps) {
  const { config, isLoading, error, isSaving, save, refetch } = useViewerConfig(companyId, productId);
  const {
    tools: apiTools,
    parts: apiParts,
    isLoading: isItemsLoading,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
  } = useProjectItems(companyId, productId);

  const [activeTab, setActiveTab] = useState<TabKey>('brand');
  const [isClosing, setIsClosing] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Local form state
  const [brandConfig, setBrandConfig] = useState<ViewerBrandConfig>({});
  const [partsConfig, setPartsConfig] = useState<ViewerPartConfig[]>([]);
  const [toolsConfig, setToolsConfig] = useState<ViewerToolConfig[]>([]);
  const [supportConfig, setSupportConfig] = useState<ViewerSupportConfig>({});
  const [filesConfig, setFilesConfig] = useState<ViewerFileConfig[]>([]);

  // Sync local state when config loads
  useEffect(() => {
    if (config) {
      setBrandConfig(config.brandConfig || {});
      setSupportConfig(config.supportConfig || {});
      setFilesConfig(config.filesConfig || []);
    }
  }, [config]);

  // Sync tools/parts from Items API
  useEffect(() => {
    if (apiTools.length > 0 || apiParts.length > 0) {
      setToolsConfig(apiTools.map((item, index) => ({
        id: item.id,
        name: item.name,
        imageUrl: item.imageUrl ?? null,
        description: item.description ?? undefined,
        order: item.sortOrder ?? index,
      })));
      setPartsConfig(apiParts.map((item, index) => ({
        id: item.id,
        name: item.name,
        quantity: 1,
        imageUrl: item.imageUrl ?? null,
        code: item.code ?? undefined,
        order: item.sortOrder ?? index,
      })));
    } else if (config) {
      // Fallback to legacy config if no items in API yet
      setPartsConfig(config.partsConfig || []);
      setToolsConfig(config.toolsConfig || []);
    }
  }, [apiTools, apiParts, config]);

  // Close animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  }, [onClose]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  // Save handler
  const handleSave = async () => {
    setStatusMessage(null);

    try {
      // Save brand, support, files via legacy viewer config
      const payload: ViewerConfigUpsertPayload = {
        brandConfig,
        supportConfig,
        filesConfig,
      };

      const configSuccess = await save(payload);

      // Save tools via Items API
      const existingToolIds = new Set(apiTools.map((t) => t.id));
      const localToolIds = new Set(toolsConfig.map((t) => t.id));

      // Delete removed tools
      for (const existing of apiTools) {
        if (!localToolIds.has(existing.id)) {
          await deleteItem(existing.id);
        }
      }

      // Create or update tools
      for (const tool of toolsConfig) {
        if (existingToolIds.has(tool.id)) {
          await updateItem(tool.id, {
            name: tool.name,
            description: tool.description ?? null,
            imageUrl: tool.imageUrl ?? null,
            sortOrder: tool.order,
          });
        } else {
          await createItem({
            itemType: 'tool',
            name: tool.name || 'Unnamed Tool',
            description: tool.description,
            imageUrl: tool.imageUrl ?? undefined,
            sortOrder: tool.order,
          });
        }
      }

      // Save parts via Items API
      const existingPartIds = new Set(apiParts.map((p) => p.id));
      const localPartIds = new Set(partsConfig.map((p) => p.id));

      // Delete removed parts
      for (const existing of apiParts) {
        if (!localPartIds.has(existing.id)) {
          await deleteItem(existing.id);
        }
      }

      // Create or update parts
      for (const part of partsConfig) {
        if (existingPartIds.has(part.id)) {
          await updateItem(part.id, {
            name: part.name,
            code: part.code ?? null,
            imageUrl: part.imageUrl ?? null,
            sortOrder: part.order,
          });
        } else {
          await createItem({
            itemType: 'part',
            name: part.name || 'Unnamed Part',
            code: part.code,
            imageUrl: part.imageUrl ?? undefined,
            sortOrder: part.order,
          });
        }
      }

      // Reorder if needed
      if (toolsConfig.length > 0) {
        const orderedToolIds = toolsConfig
          .sort((a, b) => a.order - b.order)
          .map((t) => t.id)
          .filter((id) => existingToolIds.has(id) || localToolIds.has(id));
        if (orderedToolIds.length > 0) {
          await reorderItems(orderedToolIds);
        }
      }

      if (configSuccess) {
        setStatusMessage({ text: 'Saved', type: 'success' });
        onSaveSuccess?.();
        setTimeout(() => setStatusMessage(null), 3000);
      } else {
        setStatusMessage({ text: error || 'Failed to save', type: 'error' });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save';
      setStatusMessage({ text: message, type: 'error' });
    }
  };

  // Cancel handler
  const handleCancel = () => {
    // Reset to last saved state
    if (config) {
      setBrandConfig(config.brandConfig || {});
      setSupportConfig(config.supportConfig || {});
      setFilesConfig(config.filesConfig || []);
    }
    // Reset tools/parts from API
    setToolsConfig(apiTools.map((item, index) => ({
      id: item.id,
      name: item.name,
      imageUrl: item.imageUrl ?? null,
      description: item.description ?? undefined,
      order: item.sortOrder ?? index,
    })));
    setPartsConfig(apiParts.map((item, index) => ({
      id: item.id,
      name: item.name,
      quantity: 1,
      imageUrl: item.imageUrl ?? null,
      code: item.code ?? undefined,
      order: item.sortOrder ?? index,
    })));
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="vc-panel-backdrop" onClick={handleClose} />

      {/* Panel */}
      <div className={`vc-panel ${isClosing ? 'vc-panel--closing' : ''}`}>
        {/* Header */}
        <div className="vc-panel-header">
          <h2 className="vc-panel-title">Viewer Configuration</h2>
          <button type="button" className="vc-panel-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="vc-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`vc-tab ${activeTab === tab.key ? 'vc-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="vc-tab-content">
          {isLoading || isItemsLoading ? (
            <div>
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <div className="vc-skeleton vc-skeleton--label" />
                  <div className="vc-skeleton vc-skeleton--field" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {activeTab === 'brand' && (
                <BrandConfigTab value={brandConfig} onChange={setBrandConfig} companyId={companyId} productId={productId} />
              )}
              {activeTab === 'parts' && (
                <PartsConfigTab value={partsConfig} onChange={setPartsConfig} />
              )}
              {activeTab === 'tools' && (
                <ToolsConfigTab value={toolsConfig} onChange={setToolsConfig} />
              )}
              {activeTab === 'support' && (
                <SupportConfigTab value={supportConfig} onChange={setSupportConfig} />
              )}
              {activeTab === 'files' && (
                <FilesConfigTab value={filesConfig} onChange={setFilesConfig} companyId={companyId} productId={productId} />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="vc-panel-footer">
          <button
            type="button"
            className="vc-btn vc-btn--primary"
            onClick={handleSave}
            disabled={isSaving || isLoading || isItemsLoading}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            className="vc-btn vc-btn--secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          {statusMessage && (
            <span className={`vc-panel-status vc-panel-status--${statusMessage.type}`}>
              {statusMessage.text}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
